import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  RefreshCw,
  Filter,
  Clock,
  User,
  ChevronLeft,
  ChevronRight,
  Inbox,
  UserCheck,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  Users,
} from 'lucide-react';
import { demandsApi } from '@/api/endpoints/demands.api';
import type { DemandStatus, DemandUrgency, DemandType } from '@/types/demand.types';
import {
  DemandStatusLabels,
  DemandStatusColors,
  DemandUrgencyLabels,
  DemandUrgencyColors,
  DemandTypeLabels,
} from '@/types/demand.types';

type TabType = 'all' | 'pending' | 'unassigned' | 'my';

export default function DemandsQueuePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Active tab
  const urlTab = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(urlTab || 'all');

  // Pagination
  const [page, setPage] = useState(0);
  const pageSize = 10;

  // Filters
  const urlStatus = searchParams.get('status') as DemandStatus | null;
  const urlUrgency = searchParams.get('urgency') as DemandUrgency | null;
  const urlType = searchParams.get('type') as DemandType | null;
  const [statusFilter, setStatusFilter] = useState<DemandStatus | ''>(urlStatus || '');
  const [urgencyFilter, setUrgencyFilter] = useState<DemandUrgency | ''>(urlUrgency || '');
  const [typeFilter, setTypeFilter] = useState<DemandType | ''>(urlType || '');

  // Query for stats (cached)
  const { data: stats } = useQuery({
    queryKey: ['demands', 'stats'],
    queryFn: () => demandsApi.getStats(),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Query for my demands (always needed for tab count)
  const { data: myDemands = [] } = useQuery({
    queryKey: ['demands', 'my'],
    queryFn: () => demandsApi.getMyDemands(),
    staleTime: 1000 * 60 * 2,
  });

  // Main demands query based on active tab
  const {
    data: demandsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['demands', activeTab, { page, size: pageSize, status: statusFilter, urgency: urgencyFilter, type: typeFilter }],
    queryFn: async () => {
      if (activeTab === 'my') {
        const data = await demandsApi.getMyDemands();
        return { content: data, totalPages: 1, totalElements: data.length };
      } else if (activeTab === 'pending') {
        return demandsApi.getPending({ page, size: pageSize });
      } else if (activeTab === 'unassigned') {
        return demandsApi.getUnassigned({ page, size: pageSize });
      } else {
        const params: Record<string, unknown> = { page, size: pageSize };
        if (statusFilter) params.statuses = [statusFilter];
        if (urgencyFilter) params.urgencies = [urgencyFilter];
        if (typeFilter) params.types = [typeFilter];
        return demandsApi.getAll(params as never);
      }
    },
    staleTime: 1000 * 60 * 2,
    placeholderData: keepPreviousData,
  });

  const demands = demandsData?.content || [];
  const totalPages = demandsData?.totalPages || 0;
  const totalElements = demandsData?.totalElements || 0;

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setPage(0);
    if (tab !== 'all') {
      setStatusFilter('');
      setUrgencyFilter('');
      setTypeFilter('');
    }
    const params = new URLSearchParams();
    if (tab !== 'all') params.set('tab', tab);
    setSearchParams(params, { replace: true });
  };

  const getStatusBadge = (status: DemandStatus) => {
    const colors = DemandStatusColors[status] || { bg: 'bg-gray-100', text: 'text-gray-800' };
    const label = DemandStatusLabels[status] || status;
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
        {label}
      </span>
    );
  };

  const getUrgencyBadge = (urgency: DemandUrgency) => {
    const colors = DemandUrgencyColors[urgency] || { bg: 'bg-gray-100', text: 'text-gray-800' };
    const label = DemandUrgencyLabels[urgency] || urgency;
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
        {label}
      </span>
    );
  };

  const formatWaitingTime = (minutes?: number) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return `${hours}h ${mins}min`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  };

  const formatResolutionTime = (minutes?: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const clearFilters = () => {
    setStatusFilter('');
    setUrgencyFilter('');
    setTypeFilter('');
    setPage(0);
  };

  const tabs = [
    { id: 'all' as TabType, label: 'Todas', icon: Inbox },
    { id: 'pending' as TabType, label: 'Pendentes', icon: AlertCircle, count: stats?.totalOpen },
    { id: 'unassigned' as TabType, label: 'Não Atribuídas', icon: Users },
    { id: 'my' as TabType, label: 'Minhas', icon: UserCheck, count: myDemands.length },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fila de Demandas</h1>
          <p className="text-muted-foreground">
            Triagem e encaminhamento de solicitações
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 rounded-lg border px-4 py-2 hover:bg-muted"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-blue-100 p-2">
                <Inbox className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Abertas</p>
                <p className="text-2xl font-bold">{stats.totalOpen}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-yellow-100 p-2">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Em Andamento</p>
                <p className="text-2xl font-bold">{stats.totalInProgress}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-green-100 p-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Resolvidas Hoje</p>
                <p className="text-2xl font-bold">{stats.totalResolvedToday}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-purple-100 p-2">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tempo Médio</p>
                <p className="text-2xl font-bold">
                  {formatResolutionTime(stats.averageResolutionTimeMinutes)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b">
        <nav className="-mb-px flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-muted-foreground/30 hover:text-foreground'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters - only for "all" tab */}
      {activeTab === 'all' && (
        <div className="flex flex-wrap items-center gap-4">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as DemandStatus | ''); setPage(0); }}
            className="rounded-lg border bg-background px-4 py-2"
          >
            <option value="">Todos os status</option>
            <option value="OPEN">Aberta</option>
            <option value="IN_PROGRESS">Em Andamento</option>
            <option value="WAITING_PATIENT">Aguardando Paciente</option>
            <option value="RESOLVED">Resolvida</option>
            <option value="CANCELLED">Cancelada</option>
          </select>
          <select
            value={urgencyFilter}
            onChange={(e) => { setUrgencyFilter(e.target.value as DemandUrgency | ''); setPage(0); }}
            className="rounded-lg border bg-background px-4 py-2"
          >
            <option value="">Todas as urgências</option>
            <option value="HIGH">Alta</option>
            <option value="MEDIUM">Média</option>
            <option value="LOW">Baixa</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value as DemandType | ''); setPage(0); }}
            className="rounded-lg border bg-background px-4 py-2"
          >
            <option value="">Todos os tipos</option>
            <option value="DOUBT">Dúvida</option>
            <option value="SYMPTOM">Sintoma</option>
            <option value="MEDICATION">Medicamento</option>
            <option value="APPOINTMENT">Consulta</option>
            <option value="EXAM">Exame</option>
            <option value="OTHER">Outro</option>
          </select>
          {(statusFilter || urgencyFilter || typeFilter) && (
            <button onClick={clearFilters} className="text-sm text-primary hover:underline">
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-800">Erro ao carregar demandas. Tente novamente.</p>
        </div>
      )}

      {/* Results count */}
      {!isLoading && totalElements > 0 && (
        <p className="text-sm text-muted-foreground">
          {totalElements} demanda{totalElements > 1 ? 's' : ''} encontrada{totalElements > 1 ? 's' : ''}
        </p>
      )}

      {/* Table */}
      <div className="rounded-lg border bg-card">
        {isLoading && demands.length === 0 ? (
          <div className="flex h-64 items-center justify-center">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Carregando...</span>
          </div>
        ) : demands.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
            <Inbox className="mb-4 h-12 w-12" />
            <p>
              {activeTab === 'my'
                ? 'Você não tem demandas atribuídas'
                : activeTab === 'unassigned'
                  ? 'Não há demandas sem responsável'
                  : activeTab === 'pending'
                    ? 'Não há demandas pendentes'
                    : 'Nenhuma demanda encontrada'}
            </p>
            {activeTab === 'all' && (statusFilter || urgencyFilter || typeFilter) && (
              <button onClick={clearFilters} className="mt-2 text-primary hover:underline">
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Paciente</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Tipo</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Título</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Urgência</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Responsável</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Espera</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Ações</th>
              </tr>
            </thead>
            <tbody>
              {demands.map((demand) => (
                <tr key={demand.id} className="border-b transition-colors hover:bg-muted/50">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/patients/${demand.patientId}`)}
                      className="font-medium text-primary hover:underline"
                    >
                      {demand.patientName}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {DemandTypeLabels[demand.type] || demand.type}
                  </td>
                  <td className="max-w-[200px] truncate px-4 py-3 text-sm" title={demand.title}>
                    {demand.title}
                  </td>
                  <td className="px-4 py-3">{getUrgencyBadge(demand.urgency)}</td>
                  <td className="px-4 py-3">{getStatusBadge(demand.status)}</td>
                  <td className="px-4 py-3 text-sm">
                    {demand.assignedToName ? (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {demand.assignedToName}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Não atribuído</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatWaitingTime(demand.waitingTimeMinutes)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/demands/${demand.id}`)}
                      className="text-sm text-primary hover:underline"
                    >
                      Ver detalhes
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {!isLoading && totalPages > 1 && activeTab !== 'my' && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Página {page + 1} de {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1 rounded border px-3 py-1 text-sm disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 rounded border px-3 py-1 text-sm disabled:opacity-50"
              >
                Próxima
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
